import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, CreditCard, ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useProviderAuth } from '@/hooks/auth'
import {
  useRevenueHistory,
  useRevenueSummary,
  useStripeAccountStatus,
} from '@/hooks/usePayments'
import { providerApiClient } from '@/lib/api'
import { formatCents, formatMonth } from '@/lib/format'
import ErrorState from '@/components/shared/ErrorState'
import type { StripeAccountStatus } from '@/schemas/onboarding-stripe'

function dashboardUrl(accountId?: string): string {
  return accountId
    ? `https://dashboard.stripe.com/${accountId}`
    : 'https://dashboard.stripe.com/'
}

function StatusBadge({ status }: { status: StripeAccountStatus }) {
  if (status === 'verified') {
    return (
      <Badge className="border-transparent bg-green-100 text-green-800 hover:bg-green-100">
        Verified
      </Badge>
    )
  }
  if (status === 'pending') {
    return (
      <Badge className="border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100">
        Pending
      </Badge>
    )
  }
  if (status === 'restricted') {
    return (
      <Badge className="border-transparent bg-red-100 text-red-800 hover:bg-red-100">
        Restricted
      </Badge>
    )
  }
  return (
    <Badge className="border-transparent bg-slate-100 text-slate-700 hover:bg-slate-100">
      Not connected
    </Badge>
  )
}

function statusDescription(status: StripeAccountStatus): string {
  switch (status) {
    case 'verified':
      return 'Your Stripe account is verified and ready to accept payments.'
    case 'pending':
      return 'Stripe is reviewing your information. You can continue using Determa and we’ll notify you when verification is complete.'
    case 'restricted':
      return 'Stripe needs additional information before your account can accept payments.'
    case 'not_connected':
    default:
      return 'Connect a Stripe account to start accepting payments from patients.'
  }
}

interface AlertBannerProps {
  status: StripeAccountStatus
  onAction: () => void
  actionPending: boolean
}

type AlertCopy = {
  tone: 'amber' | 'red'
  title: string
  body: string
  cta: string | null
}

const alertCopy: Record<Exclude<StripeAccountStatus, 'verified'>, AlertCopy> = {
  not_connected: {
    tone: 'amber',
    title: 'Set up payments',
    body: 'Connect a Stripe account to accept payments from patients. Stripe handles identity verification and bank linking — Determa never sees your banking details.',
    cta: 'Connect with Stripe',
  },
  pending: {
    tone: 'amber',
    title: 'Under review',
    body: 'Stripe is reviewing your information. You’ll be able to accept payments as soon as verification completes. No action needed right now.',
    cta: null,
  },
  restricted: {
    tone: 'red',
    title: 'Additional information required',
    body: 'Stripe needs additional information before your account is fully verified. Complete the remaining steps in Stripe to resume payments.',
    cta: 'Resolve with Stripe',
  },
}

function AlertBanner({ status, onAction, actionPending }: AlertBannerProps) {
  if (status === 'verified') return null

  const copy = alertCopy[status]
  const palette =
    copy.tone === 'red'
      ? 'border-red-200 bg-red-50 text-red-900'
      : 'border-amber-200 bg-amber-50 text-amber-900'

  return (
    <div className={`rounded-lg border p-4 ${palette}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-semibold">{copy.title}</p>
          <p className="text-sm">{copy.body}</p>
          {copy.cta && (
            <Button
              type="button"
              size="sm"
              onClick={onAction}
              disabled={actionPending}
            >
              {actionPending ? 'Redirecting…' : copy.cta}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function RevenueStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  )
}

export default function PaymentsSettingsPage() {
  const { provider } = useProviderAuth()
  const statusQuery = useStripeAccountStatus()
  const summaryQuery = useRevenueSummary()
  const historyQuery = useRevenueHistory(12)

  const createLink = useMutation({
    mutationFn: () =>
      providerApiClient.createStripeAccountLink(
        `${window.location.origin}/settings/payments`,
      ),
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: () => {
      toast.error('Could not open Stripe. Please try again.')
    },
  })

  const status: StripeAccountStatus = statusQuery.data?.status ?? 'not_connected'
  const accountId = provider?.stripe_account_id

  if (statusQuery.isError) {
    return (
      <ErrorState
        message="Failed to load payments information."
        onRetry={() => statusQuery.refetch()}
      />
    )
  }

  const isLoading = statusQuery.isLoading

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Payments</h1>
        <p className="mt-1 text-sm text-slate-600">
          Stripe Connect status, dashboard access, and revenue.
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="space-y-3 py-6">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4" aria-hidden />
                Stripe account
                <StatusBadge status={status} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-700">{statusDescription(status)}</p>

              <AlertBanner
                status={status}
                onAction={() => createLink.mutate()}
                actionPending={createLink.isPending}
              />

              <div className="pt-2">
                <a
                  href={dashboardUrl(accountId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-900"
                >
                  View in Stripe
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
                <p className="mt-1 text-xs text-slate-500">
                  View transactions, payouts, and tax documents in your own
                  Stripe dashboard.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Revenue this month</CardTitle>
            </CardHeader>
            <CardContent>
              {summaryQuery.isLoading ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : summaryQuery.data && summaryQuery.data.total_visits > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <RevenueStat
                    label="Visits"
                    value={String(summaryQuery.data.total_visits)}
                  />
                  <RevenueStat
                    label="Gross"
                    value={formatCents(summaryQuery.data.gross_cents)}
                  />
                  <RevenueStat
                    label="Determa fees"
                    value={formatCents(summaryQuery.data.determa_fees_cents)}
                  />
                  <RevenueStat
                    label="Net"
                    value={formatCents(summaryQuery.data.net_cents)}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500">No visits this month yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Monthly history</CardTitle>
            </CardHeader>
            <CardContent>
              {historyQuery.isLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : historyQuery.data && historyQuery.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                        <th scope="col" className="py-2 pr-3">Month</th>
                        <th scope="col" className="py-2 px-3 text-right">Visits</th>
                        <th scope="col" className="py-2 px-3 text-right">Gross</th>
                        <th scope="col" className="py-2 px-3 text-right">Determa fees</th>
                        <th scope="col" className="py-2 pl-3 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-800">
                      {historyQuery.data.map((row) => (
                        <tr key={row.month}>
                          <td className="py-2 pr-3 font-medium">{formatMonth(row.month)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{row.total_visits}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{formatCents(row.gross_cents)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{formatCents(row.determa_fees_cents)}</td>
                          <td className="py-2 pl-3 text-right tabular-nums">{formatCents(row.net_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No history yet.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
